import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import SalesOrderPicking from './SalesOrderPicking';
import { useRootStore } from '@/lib/providers/RootProvider';
import { rxPrintEnabledFor, useSendDocumentsToPrinter } from '@/lib/rxPrint';

const CLIENT_ORDER = gql`
  query salesOrder($id: ID!, $vendorId: ID!, $vendorStatuses: [String!]) {
    salesOrder(id: $id, vendorId: $vendorId, vendorStatuses: $vendorStatuses) {
      id
      vendorSalesOrderNumber
      vendorStatus
      clientStatus
      discountAmount
      shippingAmount
      taxAmount
      convenienceFeeAmount
      subtotalAmount
      totalAmount
      createdAt
      updatedAt
      orderItems {
        id
        quantity
        isShortDated
        # The two pick RULES a line can carry. Both are decided server-side (short-dated at
        # ordering, chargeback at pricing) and both make the mutation refuse stock the picker
        # would otherwise consider interchangeable, so both have to be on screen BEFORE the
        # walk to the shelf — a refusal at the scan with no badge behind it reads as a bug.
        isChargeback
        serializedItemOfStatusCount(status: "PICKED")
        unserializedItemOfStatusCount(status: "PICKED")
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
          upcProduct {
            id
            upc
          }
        }
      }
      client {
        id
        companyName
      }
      isDestructionOrder
      tote {
        id
        name
      }
      pickingSlipDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
        descriptor
      }
    }
    # Read here rather than from the root store: the root store's copy is fetched once at app mount
    # and can be a whole shift stale, and this is the screen where a stale value BLOCKS a picker
    # (with totes off they would still be told to add one; with totes newly off mid-shift the Add
    # Tote CTA would lead to a 403). Same shape as SalesOrderShipping.data.tsx — a sibling top-level
    # field in the SAME document, so it costs no extra round trip, and this page's existing
    # picking-slip poll (every 2s until the slip lands) refreshes it for free while it runs.
    # \`id\` is REQUIRED here so Apollo normalizes this to Company:<id> rather than caching it inline.
    company(id: $vendorId) {
      id
      usesTotes
      # RxPrint on: the picking slip prints to its mapped printer (see @/lib/rxPrint).
      printNodeEnabled
    }
  }
`;

const ORDER_UPDATE = gql`
  mutation salesOrderUpdate(
    $orderId: ID!
    $orderAttributes: OrderAttributes!
    $companyId: ID!
  ) {
    salesOrderUpdate(
      orderId: $orderId
      orderAttributes: $orderAttributes
      companyId: $companyId
    ) {
      id
    }
  }
`;

const SPLIT_ORDER_AT_PICKING = gql`
  mutation splitOrderAtPicking($salesOrderId: ID!, $vendorId: ID!) {
    splitOrderAtPicking(salesOrderId: $salesOrderId, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      clientStatus
      discountAmount
      shippingAmount
      taxAmount
      convenienceFeeAmount
      subtotalAmount
      totalAmount
      createdAt
      updatedAt
      orderItems {
        id
        quantity
        isShortDated
        # Keep in step with CLIENT_ORDER above. This payload re-seeds the screen's order after the
        # split, so a pick-rule flag selected there but omitted here comes back undefined and the
        # badge quietly disappears from the split CO — on exactly the lines a picker is about to
        # go and fill.
        isChargeback
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
        }
      }
      tote {
        id
      }
      client {
        id
        companyName
      }
    }
  }
`;

// The order's lines ranked along the floor's pick walk. A SEPARATE document from the order:
// it is re-read on its own whenever the picker re-anchors the walk (\`startZoneId\`), and the
// order document is polled for the picking slip without dragging this along each time.
export const SALES_ORDER_PICK_PATH = gql`
  query SalesOrderPickPath($companyId: ID!, $salesOrderId: ID!, $startZoneId: ID) {
    salesOrderPickPath(
      companyId: $companyId
      salesOrderId: $salesOrderId
      startZoneId: $startZoneId
    ) {
      pathConfigured
      anchorSource
      anchorOnPath
      direction
      anchorZone {
        id
        name
        pickSequence
      }
      stops {
        orderItemId
        rank
        zone {
          id
          name
          zoneType
          pickSequence
        }
        zones {
          id
          name
          zoneType
          pickSequence
        }
      }
    }
  }
`;

function SalesOrderPickingData(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const [orderUpdate] = useMutation(ORDER_UPDATE);
  const [splitOrderAtPicking] = useMutation(SPLIT_ORDER_AT_PICKING);

  const { data, error, loading, refetch } = useQuery(CLIENT_ORDER, {
    fetchPolicy: 'network-only',
    variables: { id, vendorId, vendorStatuses: ['picking'] }
  });

  // Held in the URL rather than in state so it survives the round trip to a pick-scan screen
  // and back. Once the first unit is picked the server anchors on that pick by itself, so a
  // lost param after that point changes nothing.
  const startZoneId = searchParams.get('startZoneId') || null;
  const setStartZoneId = (zoneId: string | null) =>
    setSearchParams(zoneId ? { startZoneId: zoneId } : {}, { replace: true });

  const {
    data: pickPathData,
    error: pickPathError,
    loading: pickPathLoading
  } = useQuery(SALES_ORDER_PICK_PATH, {
    fetchPolicy: 'network-only',
    variables: { companyId: vendorId, salesOrderId: id, startZoneId }
  });

  const { salesOrder, company } = data || {};
  const { send: sendToPrinter, sendingKey } = useSendDocumentsToPrinter(vendorId as string);
  const orderId = salesOrder?.id;
  const slipReady = !!salesOrder?.pickingSlipDocument?.link;

  useEffect(() => {
    if (loading || slipReady || !orderId) return undefined;
    const poll = setInterval(() => {
      refetch();
    }, 2000);
    const timeout = setTimeout(() => clearInterval(poll), 30000);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [loading, orderId, slipReady, refetch]);

  if (loading || (pickPathLoading && !pickPathData)) return <Spinner />;
  if (error) return <ApiError error={error} />;
  if (pickPathError) return <ApiError error={pickPathError} />;
  if (!salesOrder) {
    navigate('/sales-orders', { replace: true });
    return <Spinner />;
  }

  return (
    <SalesOrderPicking
      salesOrder={salesOrder}
      company={company}
      companyId={vendorId}
      orderUpdate={orderUpdate}
      splitOrderAtPicking={splitOrderAtPicking}
      pickPath={pickPathData?.salesOrderPickPath}
      setStartZoneId={setStartZoneId}
      rxPrintEnabled={rxPrintEnabledFor(company)}
      sendingKey={sendingKey}
      onSendToPrinter={(key, documents) => sendToPrinter(key, salesOrder.id, documents)}
      {...props}
    />
  );
}

export default SalesOrderPickingData;
